/* state/transit-data.js — lazy-hämtar hållplats-index per kollektivtrafik-lager.
 *
 * Kart-tiles (overlays/{buss,farjor,tunnelbana}.pmtiles) bär bara lätta
 * linje-properties (route_id, linjenr, namn, operatör, n_stops). Hela
 * hållplatslistan per linje ligger i data/_generated/transit/<lager>_stops.json
 * och hämtas först vid klick (buss-indexet är ~6 MB → aldrig vid page-load).
 *
 * Index-format: { route_id: { short, long, agency, headsign, rtype, dur_min,
 *                             stops: [ { n, lat, lon, t }, ... ] } }
 *   dur_min = end-to-end-restid (min), t = minuter från första hållplatsen.
 *
 * Public API:
 *   getTransitLine(layer, routeId) -> Promise<record|null>
 *   getTransitBounds(layer, predicate) -> Promise<[[w,s],[e,n]]|null>
 *   getHubs() -> Promise<{ hubId: {n,lat,lon,tier,modes,nlines,lines} }|null>
 *   getHub(hubId) -> Promise<record|null>
 *   findStopNear(lon, lat, name) -> Promise<{n,lat,lon,hub?,lines}|null>
 */

// Bumpa när <lager>_stops.json byggs om (scripts/build_transit_lines.py).
// transit3: spårväg flyttad buss→tunnelbana-lagret + route_color-prop.
// transit4: SL-pendeltåg (rtype 106) tillagt i tunnelbana-lagret.
// transit5: nytt tåg-lager (reguljära tåglinjer, grupperade per korridor;
//           bär dur_min/n_dep/first_dep/last_dep + per-stopp "t"-tid).
// transit6: korrupta GTFS-koordinater nollade (alla lager) — se BUGGLOGG.
// transit7: knytpunkts-index (hubs.json) + hub-id bakat på varje hållplats.
// transit8: knytpunkts-tier omgraderad → 4 nivåer (mega-knytpunkt tillagd).
// transit9: restid i ALLA lager — `t` per hållplats + `dur_min` per linje även
//           för buss/tunnelbana/färja (förut bara tåg).
const BUST = 'v=transit9';

const KNOWN = { buss: true, farjor: true, tunnelbana: true, tag: true };

// layer -> Promise<indexObj|null>. En fetch per lager, cachad för sessionen.
const cache = new Map();

function fetchIndex(layer) {
    if (!KNOWN[layer]) return Promise.resolve(null);
    if (cache.has(layer)) return cache.get(layer);
    const p = fetch(`/data/_generated/transit/${layer}_stops.json?${BUST}`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    cache.set(layer, p);
    return p;
}

export async function getTransitLine(layer, routeId) {
    if (routeId == null) return null;
    const idx = await fetchIndex(layer);
    if (!idx) return null;
    return idx[routeId] || null;
}

/* Rutt-id:n i `layer` vars record matchar `predicate(record)`. Fokusläget
 * frågar "vilka linjer STANNAR i den här platsen" — en fråga geometrin inte kan
 * svara på: en linje som passerar en by utan station rör byn på kartan men inte
 * i verkligheten (mätt på Lilla Tollstad: 10 av 10 visade tåglinjer passerade
 * utan att stanna). Returnerar null när indexet inte gick att hämta, så
 * anroparen kan skilja "inga träffar" från "inget svar". */
export async function getTransitRoutes(layer, predicate) {
    const idx = await fetchIndex(layer);
    if (!idx) return null;
    const ut = [];
    for (const rid in idx) {
        if (idx[rid] && predicate(idx[rid])) ut.push(rid);
    }
    return ut;
}

/* Bounding-box över alla hållplatser för de linjer i `layer` vars record
 * matchar `predicate(record)`. Används av den klickbara legenden för att
 * flyga till t.ex. röda linjen. Returnerar [[west,south],[east,north]] (samma
 * form som MapLibre fitBounds tar) eller null om inga träffar/data saknas. */
export async function getTransitBounds(layer, predicate) {
    const idx = await fetchIndex(layer);
    if (!idx) return null;
    let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity, count = 0;
    for (const rid in idx) {
        const rec = idx[rid];
        if (!rec || !predicate(rec)) continue;
        for (const st of (rec.stops || [])) {
            if (st.lon < w) w = st.lon;
            if (st.lon > e) e = st.lon;
            if (st.lat < s) s = st.lat;
            if (st.lat > n) n = st.lat;
            count++;
        }
    }
    if (!count) return null;
    return [[w, s], [e, n]];
}

// ── Knytpunkter (hubs) ──────────────────────────────────────────────────
// hubs.json: { hubId: { n, lat, lon, tier(1-3|0), modes:[…], nlines,
//   lines:[ { m:mode, l:label, r:ref(route_id/grupp-id), g:layer } ] } }
// Klustrade hållplatser där ≥2 linjer möts (bygg: build_transit_lines.py).
// Laddas lazy: klick på en hållplats (klick-rutan) resp. när knytpunkts-
// lagret tänds. En fetch, cachad för sessionen.
let hubsPromise = null;
let hubsIdx = null;      // satt när fetchen landat — för synkrona uppslag vid klick

export function getHubs() {
    if (hubsPromise) return hubsPromise;
    hubsPromise = fetch(`/data/_generated/transit/hubs.json?${BUST}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((idx) => { hubsIdx = idx; return idx; })
        .catch(() => null);
    return hubsPromise;
}

export async function getHub(hubId) {
    if (hubId == null) return null;
    const idx = await getHubs();
    return idx ? (idx[hubId] || null) : null;
}

// ── Uppslag på position (basemap-ens egna OSM-hållplatser) ──────────────
// Kartans OSM-prickar (poi_transit i liberty/dark-matter) bär bara namn och
// class — ingen route-koppling. De matchas mot hållplats-indexet på läge.
const NEAR_M = 200;   // utan namnträff
const NAME_M = 700;   // med namnträff

function metersBetween(lon1, lat1, lon2, lat2) {
    const dy = (lat2 - lat1) * 111320;
    const dx = (lon2 - lon1) * 111320 * Math.cos(lat1 * Math.PI / 180);
    return Math.sqrt(dx * dx + dy * dy);
}

// "Knivsta station (Knivsta)" och "Knivsta" ska jämföras lika: parentesled bort,
// allt utom a-z0-9 till mellanslag, och de ord som bara betyder "det är en
// hållplats". Å/ä/ö faller bort på båda sidor av jämförelsen och behöver därför
// ingen NFD-avkodning — "Västerås C" och "Västerås station" blir samma sträng.
const NOISE_WORDS = /\b(station|centralstation|central|hallplats|hpl|busstation|bussterminal|terminal|resecentrum)\b/g;
function foldName(s) {
    return String(s || '')
        .toLowerCase()
        .replace(/\([^)]*\)/g, ' ')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(NOISE_WORDS, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
function namesMatch(a, b) {
    if (!a || !b || a.length < 3 || b.length < 3) return false;
    return a === b || a.indexOf(b) !== -1 || b.indexOf(a) !== -1;
}

/* ── Hållplats-index per rutnätsruta ────────────────────────────────────
 * `hubs.json` täcker bara KNYTPUNKTER (≥2 linjer), och 60 % av landets
 * hållplatser trafikeras av en enda linje — de fanns inte där alls. Rut-
 * indexet (scripts/build_transit_stopp_index.py) täcker ALLA, och en ruta väger
 * kilobytes i stället för 6,5 MB. Rutstorleken MÅSTE vara samma som i
 * byggscriptet, annars pekar klienten på en fil som inte finns. */
const LAT_CELL = 0.125;
const LON_CELL = 0.25;
const cellCache = new Map();   // "i_j" → Promise<array|null>, en fetch per ruta

function fetchCell(lat, lon) {
    const key = Math.floor(lat / LAT_CELL) + '_' + Math.floor(lon / LON_CELL);
    if (cellCache.has(key)) return cellCache.get(key);
    const p = fetch(`/data/_generated/transit/stopp/${key}.json?${BUST}`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    cellCache.set(key, p);
    return p;
}

/* Hållplatsen närmast en punkt, eller null: { n, lat, lon, hub?, lines[] }.
 * Matchar på LÄGE med namnet som förstärkning — OSM-noden ligger ofta någon
 * hundra meter från GTFS-klustrets tyngdpunkt (stationshus vs perronger), men
 * bara när namnen är desamma vågar man lita på det avståndet. Kluster närmare
 * än 800 m en rutkant ligger i BÅDA rutorna, så en enda hämtning räcker. */
export async function findStopNear(lon, lat, name) {
    if (typeof lon !== 'number' || typeof lat !== 'number') return null;
    const poster = await fetchCell(lat, lon);
    if (!poster || !poster.length) return null;
    const want = foldName(name);
    let best = null, bestScore = Infinity;
    for (const s of poster) {
        if (!s || typeof s.lat !== 'number' || typeof s.lon !== 'number') continue;
        const d = metersBetween(lon, lat, s.lon, s.lat);
        const named = want && namesMatch(want, foldName(s.n));
        if (d > (named ? NAME_M : NEAR_M)) continue;
        // Namnträff väger tyngre än rena meter — annars vinner grannhållplatsen.
        const score = named ? d * 0.25 : d;
        if (score < bestScore) { bestScore = score; best = s; }
    }
    return best;
}
